<template>
  <div class="question-difficulty-trend">
    <el-card class="chart-card full-width">
      <div slot="header" class="header">
        <div class="title">题目难度趋势</div>
        <div class="operations">
        <el-select
          v-model="selectedLevels"
          multiple
          collapse-tags
          placeholder="选择难度等级"
          class="type-select"
          @change="handleLevelChange"
        >
          <div class="select-actions">
            <el-button size="mini" type="text" @click.stop.prevent="handleSelectAll">全选</el-button>
            <el-divider direction="vertical" />
            <el-button size="mini" type="text" @click.stop.prevent="handleReset">重置</el-button>
          </div>
          <el-option
            v-for="level in difficultyLevels"
            :key="level"
            :label="level"
            :value="level"
          />
        </el-select>
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExport" />
          </el-tooltip>
          <el-tooltip content="放大显示" placement="top">
            <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreen" />
          </el-tooltip>
        </div>
      </div>
      <div class="chart-container" ref="chart">
        <div class="chart-content">
          <div class="y-axis-label">题目数量</div>
          <div class="y-axis-label right">得分率</div>
        </div>
      </div>
    </el-card>

    <!-- 全屏弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="90%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog vertical-center-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
      @close="handleDialogClose"
    >
      <div slot="title" class="dialog-header">
        <span>题目难度趋势</span>
        <div class="dialog-operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          </el-tooltip>
          <el-tooltip content="关闭" placement="top">
            <el-button type="text" icon="el-icon-close" @click="closeDialog" />
          </el-tooltip>
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import _ from 'lodash'

export default {
  name: 'QuestionDifficultyTrend',
  data() {
    return {
      chart: null,
      chartData: {
        months: [],
        difficulties: [],
        series: [],
        scores: {}
      },
      fullscreenChart: null,
      dialogVisible: false,
      resizeHandler: null,
      isInitializing: false,
      selectedLevels: [],
      difficultyLevels: ['未分类', '红题', '橙题', '黄题', '绿题', '蓝题', '紫题', '黑题'],
      difficultyColors: {
        '未分类': '#D9D9D9',  // 灰色
        '红题': '#F56C6C',    // 红色
        '橙题': '#E6A23C',    // 橙色
        '黄题': '#F7BA2A',    // 黄色
        '绿题': '#67C23A',    // 绿色
        '蓝题': '#409EFF',    // 蓝色
        '紫题': '#9B59B6',    // 紫色
        '黑题': '#2C3E50'     // 黑色
      },
      resizeObserver: null,
      visibilityObserver: null
    }
  },
  mounted() {
    this.initChart()
    this.selectedLevels = [...this.difficultyLevels]
    this.fetchData()
    window.addEventListener('resize', this.resizeChart)
    this.setupResizeObserver()
    this.setupVisibilityObserver()
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
    if (this.fullscreenChart) {
      this.fullscreenChart.dispose()
    }
    window.removeEventListener('resize', this.resizeChart)
    if (this.resizeObserver) {
      try { this.resizeObserver.disconnect() } catch (e) {}
      this.resizeObserver = null
    }
    if (this.visibilityObserver) {
      try { this.visibilityObserver.disconnect() } catch (e) {}
      this.visibilityObserver = null
    }
  },
  methods: {
    initChart() {
      this.$nextTick(() => {
        const el = this.$refs.chart
        if (!el) return
        this.chart = echarts.init(el)
        // 初次确保尺寸正确
        setTimeout(() => { this.chart && this.chart.resize() }, 0)
      })
    },
    resizeChart() {
      this.chart?.resize()
    },
    forceResize() {
      // 展开后强制完整重绘，规避初始尺寸为0导致的空白
      try {
        if (!this.chart) {
          this.initChart()
        }
        // 重新应用当前数据的配置
        this.updateChart()
        this.chart && this.chart.resize()
      } catch (e) {}
    },
    setupResizeObserver() {
      const el = this.$refs.chart
      if (!el || typeof ResizeObserver === 'undefined') return
      this.resizeObserver = new ResizeObserver(() => {
        if (this.chart) {
          this.chart.resize()
        }
      })
      this.resizeObserver.observe(el)
    },
    setupVisibilityObserver() {
      const rootEl = this.$el
      if (!rootEl || typeof IntersectionObserver === 'undefined') return
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // 进入可见区域后，稍等布局稳定再重绘
            setTimeout(() => this.forceResize(), 60)
          }
        })
      }, { root: null, threshold: 0.01 })
      this.visibilityObserver.observe(rootEl)
    },
    async fetchData() {
      try {
        const res = await request.get('/api/analysis/question/difficulty_trend')
        this.chartData = {
          months: res.data.months || [],
          difficulties: res.data.difficulties || [],
          series: res.data.series || [],
          scores: res.data.scores || {}
        }
        this.updateChart()
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败')
      }
    },
    calculateMonthlyAverages() {
      const monthlyData = new Map()
      
      this.chartData.months.forEach((month, index) => {
        let totalScore = 0
        let scoreCount = 0
        
        // 计算选中难度的总和
        this.selectedLevels.forEach(difficulty => {
          const score = this.chartData.scores[difficulty]?.[index]
          const count = this.chartData.series.find(s => s.name === difficulty)?.data[index] || 0
          
          // 只有当该难度在这个月有题目时才计入平均值
          if (count > 0 && score !== null && score !== undefined) {
            totalScore += score * count  // 根据题目数量加权
            scoreCount += count
          }
        })
        
        // 计算加权平均值
        monthlyData.set(month, {
          avgScore: scoreCount > 0 ? Math.round(totalScore / scoreCount * 100) / 100 : null
        })
      })

      return Array.from(monthlyData.entries()).map(([month, data]) => ({
        month,
        avgScore: data.avgScore
      })).sort((a, b) => a.month.localeCompare(b.month))
    },
    updateChart() {
      const el = this.$refs.chart
      if (!el) return
      const w = el.clientWidth
      const h = el.clientHeight
      if (!w || !h) {
        // 容器尚未就绪，稍后重试
        setTimeout(() => { this.updateChart() }, 120)
        return
      }
      const monthlyAverages = this.calculateMonthlyAverages()
      
      // 重新构建柱状图数据
      const barData = this.chartData.months.map((_, index) => {
        const monthData = {}
        this.selectedLevels.forEach(difficulty => {
          const series = this.chartData.series.find(s => s.name === difficulty)
          if (series) {
            monthData[difficulty] = series.data[index] || 0
          }
        })
        return monthData
      })

      // 构建新的系列数据
      const filteredSeries = this.selectedLevels.map(difficulty => ({
        name: difficulty,
        type: 'bar',
        stack: 'total',
        barMaxWidth: 50,
        itemStyle: {
          color: this.difficultyColors[difficulty],  // 使用固定颜色
          borderRadius: [4, 4, 0, 0]
        },
        emphasis: {
          focus: 'series'
        },
        data: barData.map(month => month[difficulty] || 0)
      }))
      
      const option = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          formatter: function(params) {
            if (!params || !Array.isArray(params) || params.length === 0) return ''
            const title = params[0].axisValue || ''
            
            // 获取难度标签样式
            const getDifficultyTagStyle = (difficulty) => {
              const colorMap = {
                '未分类': { bg: 'rgba(217, 217, 217, 0.2)', border: '#D9D9D9', color: '#e8e8e8' },
                '红题': { bg: 'rgba(245, 108, 108, 0.2)', border: '#F56C6C', color: '#ff9999' },
                '橙题': { bg: 'rgba(230, 162, 60, 0.2)', border: '#E6A23C', color: '#ffb84d' },
                '黄题': { bg: 'rgba(247, 186, 42, 0.2)', border: '#F7BA2A', color: '#ffd966' },
                '绿题': { bg: 'rgba(103, 194, 58, 0.2)', border: '#67C23A', color: '#95de64' },
                '蓝题': { bg: 'rgba(64, 158, 255, 0.2)', border: '#409EFF', color: '#73b9ff' },
                '紫题': { bg: 'rgba(155, 89, 182, 0.2)', border: '#9B59B6', color: '#bb7acc' },
                '黑题': { bg: 'rgba(44, 62, 80, 0.2)', border: '#2C3E50', color: '#5a6c7d' }
              }
              const style = colorMap[difficulty] || colorMap['未分类']
              return `display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 11px; background-color: ${style.bg}; border: 1px solid ${style.border}; color: ${style.color}; line-height: 1.2;`
            }
            
            let content = ''
            params.forEach(param => {
              if (param.seriesName === '月度平均得分') {
                const value = param.value != null ? param.value.toFixed(1) + '%' : '-'
                content += `<div>${param.seriesName}：${value}</div>`
              } else if (param.value != null && param.value > 0) {
                // 检查是否是难度等级（在 difficultyLevels 中）
                const isDifficulty = this.difficultyLevels.includes(param.seriesName)
                if (isDifficulty) {
                  const tagStyle = getDifficultyTagStyle(param.seriesName)
                  content += `<div><span style="${tagStyle}">${param.seriesName}</span>：${param.value}题</div>`
                } else {
                  content += `<div>${param.seriesName}：${param.value}题</div>`
                }
              }
            })
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${title}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  ${content}
                </div>
              </div>
            `
          }.bind(this)
        },
        legend: {
          data: [...this.selectedLevels, '月度平均得分'],
          bottom: 0,
          left: 'center',
          itemGap: 20,
          textStyle: {
            fontSize: 12
          }
        },
        grid: {
          left: '0%',
          right: '0%',
          bottom: '15%',
          top: '30px',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.chartData.months,
          axisLabel: {
            interval: 0,
            rotate: 45,
            fontSize: 12,
            margin: 8
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '题目数量',
            minInterval: 1,
            position: 'left',
            axisLabel: {
              formatter: '{value}',
              margin: 8
            },
            nameTextStyle: {
              padding: [0, 15, 0, 20]
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed'
              }
            }
          },
          {
            type: 'value',
            name: '得分率',
            min: 0,
            max: 100,
            interval: 20,
            position: 'right',
            offset: 15,
            axisLabel: {
              formatter: '{value}%',
              margin: 8,
              align: 'right'
            },
            nameTextStyle: {
              padding: [0, -7, 0, 20],
              align: 'right'
            },
            splitLine: {
              show: false
            }
          }
        ],
        series: [
          ...filteredSeries,
          {
            name: '月度平均得分',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            lineStyle: {
              width: 2,
              color: '#67C23A'
            },
            itemStyle: {
              color: '#67C23A'
            },
            emphasis: {
              focus: 'series'
            },
            z: 2,
            data: monthlyAverages.map(item => ({
              value: item.avgScore,
              name: item.month
            }))
          }
        ]
      }

      if (!this.chart) return
      // 清空后再设置，避免多次切换后残留状态
      try { this.chart.clear() } catch (e) {}
      this.chart.setOption(option, true)
      // 布局变化后强制两次重算（一次同步，一次异步）
      try { this.chart.resize() } catch (e) {}
      this.$nextTick(() => { setTimeout(() => { this.chart && this.chart.resize() }, 0) })
    },
    handleExport() {
      const dataURL = this.chart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })
      
      const link = document.createElement('a')
      link.download = '题目难度趋势.png'
      link.href = dataURL
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    handleDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.fullscreenChart
          if (!chartDom) return

          this.fullscreenChart = echarts.init(chartDom)
          const option = this.chart.getOption()
          
          option.grid = {
            left: '5%',
            right: '7%',
            bottom: '10%',
            top: '10%',
            containLabel: true
          }

          this.fullscreenChart.setOption(option)
          
          this.resizeHandler = _.debounce(() => {
            if (this.fullscreenChart) {
              this.fullscreenChart.resize()
            }
          }, 100)

          window.addEventListener('resize', this.resizeHandler)
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleDialogClose() {
      this.cleanupFullscreen()
    },
    handleDialogClosed() {
      this.isInitializing = false
    },
    cleanupFullscreen() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }

      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    handleFullscreen() {
      this.cleanupFullscreen()
      this.dialogVisible = true
    },
    closeDialog() {
      this.dialogVisible = false
    },
    handleExportFullscreen() {
      if (!this.fullscreenChart) return
      
      try {
        const dataURL = this.fullscreenChart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff'
        })
        
        const link = document.createElement('a')
        link.download = '题目难度趋势.png'
        link.href = dataURL
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    handleLevelChange() {
      this.fetchData()
    },
    handleSelectAll() {
      this.selectedLevels = [...this.difficultyLevels]
      this.fetchData()
    },
    handleReset() {
      this.selectedLevels = []
      this.fetchData()
    }
  },
  watch: {
    dialogVisible(val) {
      if (!val && this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    selectedLevels: {
      handler(newVal) {
        if (this.chart) {
          this.updateChart()
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.question-difficulty-trend {
  width: 100%;
  height: 100%;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
}

.title {
  font-weight: normal;
  color: #ffffff;
}

.operations {
  display: flex;
  gap: 8px;
  align-items: center;
}

.operations .el-button {
  color: #ffffff;
}

.operations .el-button:hover {
  color: #BD6C73;
}

.filter-select :deep(.el-input__inner) {
  background-color: #FFFFFF;
  border-radius: 4px;
  border: none;
  color: #606266;
  padding-left: 12px;
}

.filter-select :deep(.el-input__prefix) {
  left: 10px;
  color: #909399;
}

.filter-select :deep(.el-select__tags) {
  background-color: transparent;
}

.filter-select :deep(.el-tag) {
  background-color: #F5F7FA;
  border-color: transparent;
  color: #606266;
  border-radius: 2px;
  margin-right: 4px;
}

.filter-select :deep(.el-tag__close) {
  color: #606266;
  background-color: transparent;
}

.filter-select :deep(.el-tag__close:hover) {
  background-color: transparent;
  color: #909399;
}

.filter-select :deep(.el-select__input) {
  color: #606266;
  margin-left: 0;
}

.chart-container {
  flex: 1;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  height: 400px; /* 确保容器有高度，图表可渲染 */
  position: relative; /* 使内部覆盖层不影响布局 */
}

.chart-content {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.el-button [class^="el-icon-"] {
  font-size: 16px;
}

.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}

.dialog-operations {
  display: flex;
  gap: 8px;
}

.dialog-operations .el-button {
  color: #ffffff;
}

.dialog-operations .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 600px;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

.operations :deep(.el-select) {
  width: 140px;
  margin-right: 16px;
}

.operations :deep(.el-select .el-input__inner) {
  height: 28px;
  line-height: 28px;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: #fff;
  padding-right: 25px;
}

.operations :deep(.el-select .el-input__inner:hover) {
  background-color: rgba(255, 255, 255, 0.2);
}

.operations :deep(.el-select .el-input__suffix) {
  right: 5px;
}

.operations :deep(.el-select .el-select__caret) {
  color: #fff;
  line-height: 28px;
}

.operations :deep(.el-select .el-select__tags) {
  background-color: transparent;
  height: 28px;
  line-height: 28px;
  padding: 0 4px;
}

.operations :deep(.el-select .el-tag) {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: transparent;
  color: #fff;
  height: 20px;
  line-height: 20px;
  margin: 4px 0;
  border-radius: 2px;
}

.operations :deep(.el-select .el-tag__close) {
  color: #fff;
  background-color: transparent;
}

.operations :deep(.el-select .el-tag__close:hover) {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.3);
}

.operations :deep(.el-select .el-select__input) {
  color: #fff;
  margin-left: 0;
}

.operations :deep(.el-select-dropdown__item) {
  height: 34px;
  line-height: 34px;
}

.operations :deep(.el-select-dropdown__item.selected) {
  color: #409EFF;
  font-weight: bold;
}

.operations :deep(.el-select-dropdown__item.hover) {
  background-color: rgba(64, 158, 255, 0.1);
}

.type-select {
  margin-right: 16px;
  width: 160px;
}

.type-select :deep(.el-input__inner) {
  height: 28px;
  line-height: 28px;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: #fff;
  padding-right: 25px;
}

.type-select :deep(.el-input__inner:hover) {
  background-color: rgba(255, 255, 255, 0.2);
}

.type-select :deep(.el-input__suffix) {
  right: 5px;
}

.type-select :deep(.el-select__caret) {
  color: #fff;
  line-height: 28px;
}

.type-select :deep(.el-select__tags) {
  background-color: transparent;
  height: 28px;
  line-height: 28px;
  padding: 0 4px;
}

.type-select :deep(.el-tag) {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: transparent;
  color: #fff;
  height: 20px;
  line-height: 20px;
  margin: 4px 0;
  border-radius: 2px;
}

.type-select :deep(.el-tag__close) {
  color: #fff;
  background-color: transparent;
}

.type-select :deep(.el-tag__close:hover) {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.3);
}

.type-select :deep(.el-select__input) {
  color: #fff;
  margin-left: 0;
}

.type-select :deep(.el-select-dropdown__item) {
  height: 34px;
  line-height: 34px;
}

.type-select :deep(.el-select-dropdown__item.selected) {
  color: #409EFF;
  font-weight: bold;
}

.type-select :deep(.el-select-dropdown__item.hover) {
  background-color: rgba(64, 158, 255, 0.1);
}

.select-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-bottom: 1px solid #EBEEF5;
  width: 100%;
  height: 34px;
  line-height: 34px;
}

.select-actions .el-button {
  font-size: 14px;
  padding: 0;
  height: 34px;
  line-height: 34px;
  flex: 1;
  text-align: center;
}

.select-actions .el-divider--vertical {
  height: 14px;
  margin: 0 2px;
}

.type-select :deep(.el-select-dropdown__wrap) {
  margin-bottom: 0;
  max-height: 240px;
}

.type-select :deep(.el-select-dropdown__item) {
  height: 34px;
  line-height: 34px;
  font-size: 14px;
}

.type-select :deep(.el-divider--horizontal) {
  margin: 1px 0;
}
</style> 